import type { Resident } from '@lmc/ts-common'
import { getJSON } from 'common/services/api'

import { useQuery } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import { RESIDENTS_QUERY_KEY, RESIDENTS_URL } from './queryConstants'

type UseResidentsListResponse = {
  residents: Resident[]
  isLoading: boolean
  refetchResidents: () => void
}

type UseResidentsList = {
  filters?: Record<string, any>
}

/**
 * Deliberately does NOT prime the per-resident `['resident', id]` cache from
 * this response. The request is a projection (see `fields` below), and that key
 * is read by `useResident` and SignatureModal, which expect a whole resident —
 * writing a projected one there made the profile crash on `residentAllergies`
 * and read every unrequested field as absent (CM-4671).
 */
const useResidentsList = ({
  filters
}: UseResidentsList): UseResidentsListResponse => {
  const params = getQueryParams({
    fields: [
      'id',
      'address',
      'addresses',
      'name',
      'picture',
      'status',
      'gender',
      'location',
      'carePackage',
      'dnacpr',
      'totalResidentMinutes'
    ],
    ...filters
  })

  const {
    isLoading: isLoadingResidents,
    data: residentsData,
    refetch
  } = useQuery<Resident[]>(
    [RESIDENTS_QUERY_KEY, params],
    () =>
      getJSON({
        url: `${RESIDENTS_URL}?${params}`,
        isRota: true
      }),
    {
      retry: 1
    }
  )

  return {
    residents: residentsData || [],
    isLoading: isLoadingResidents,
    refetchResidents: refetch
  }
}

export default useResidentsList
